<template>
  <div class="statuses-container">
    <h1>Управление статусами</h1>
    
    <div class="statuses-content">
      <div class="statuses-list">
        <div class="statuses-header">
          <h2>Список статусов</h2>
          <button @click="showNewStatusForm = true" class="add-status-button">
            <i class="fas fa-plus"></i> Добавить статус
          </button>
        </div>
        
        <div v-if="loading" class="loading">
          Загрузка статусов...
        </div>
        
        <div v-else-if="statuses.length === 0" class="no-statuses">
          Статусы не найдены. Создайте первый статус.
        </div>
        
        <div v-else class="statuses-table-container">
          <table class="statuses-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Цвет</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="status in statuses" :key="status._id">
                <td>
                  <div v-if="editingStatus && editingStatus._id === status._id" class="edit-field">
                    <input type="text" v-model="editingStatus.name" placeholder="Название статуса">
                  </div>
                  <div v-else class="status-name">{{ status.name }}</div>
                </td>
                <td>
                  <div v-if="editingStatus && editingStatus._id === status._id" class="edit-field">
                    <input type="color" v-model="editingStatus.color">
                  </div>
                  <div v-else class="status-color">
                    <div class="color-preview" :style="{ backgroundColor: status.color }"></div>
                    <span class="color-value">{{ status.color }}</span>
                  </div>
                </td>
                <td>
                  <div v-if="editingStatus && editingStatus._id === status._id" class="status-actions">
                    <button @click="saveStatus" class="save-button">
                      <i class="fas fa-save"></i> Сохранить
                    </button>
                    <button @click="cancelEdit" class="cancel-button">
                      <i class="fas fa-times"></i> Отмена
                    </button>
                  </div>
                  <div v-else class="status-actions">
                    <button @click="editStatus(status)" class="edit-button">
                      <i class="fas fa-edit"></i> Изменить
                    </button>
                    <button @click="confirmDeleteStatus(status)" class="delete-button">
                      <i class="fas fa-trash"></i> Удалить
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div v-if="showNewStatusForm" class="new-status-form">
        <h2>Новый статус</h2>
        
        <div class="form-group">
          <label for="statusName">Название:</label>
          <input type="text" id="statusName" v-model="newStatus.name" placeholder="Введите название статуса">
        </div>
        
        <div class="form-group">
          <label for="statusColor">Цвет:</label>
          <input type="color" id="statusColor" v-model="newStatus.color">
          <span class="color-value">{{ newStatus.color }}</span>
        </div>
        
        <div class="form-actions">
          <button @click="createStatus" class="create-button" :disabled="!newStatus.name">
            <i class="fas fa-plus"></i> Создать
          </button>
          <button @click="showNewStatusForm = false" class="cancel-button">
            <i class="fas fa-times"></i> Отмена
          </button>
        </div>
      </div>
    </div>
    
    <div v-if="showDeleteConfirm" class="delete-confirm-modal">
      <div class="delete-confirm-content">
        <h3>Подтверждение удаления</h3>
        <p>Вы уверены, что хотите удалить статус "{{ statusToDelete.name }}"?</p>
        <div class="delete-confirm-actions">
          <button @click="deleteStatus" class="confirm-button">
            <i class="fas fa-trash"></i> Удалить
          </button>
          <button @click="cancelDelete" class="cancel-button">
            <i class="fas fa-times"></i> Отмена
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';

export default {
  name: 'StatusesView',
  setup() {
    const statuses = ref([]);
    const loading = ref(false);
    const error = ref(null);
    
    const showNewStatusForm = ref(false);
    const newStatus = ref({
      name: '',
      color: '#2196f3'
    });
    
    const editingStatus = ref(null);
    
    const showDeleteConfirm = ref(false);
    const statusToDelete = ref(null);
    
    const fetchStatuses = async () => {
      try {
        loading.value = true;
        const response = await axios.get('/statuses');
        statuses.value = response.data;
      } catch (error) {
        console.error('Ошибка при получении статусов:', error);
      } finally {
        loading.value = false;
      }
    };
    
    const createStatus = async () => {
      try {
        if (!newStatus.value.name) {
          return;
        }
        
        loading.value = true;
        const response = await axios.post('/statuses', {
          name: newStatus.value.name,
          color: newStatus.value.color
        });
        
        statuses.value.push(response.data);
        
        // Сбрасываем форму
        newStatus.value = {
          name: '',
          color: '#2196f3'
        };
        showNewStatusForm.value = false;
      } catch (error) {
        console.error('Ошибка при создании статуса:', error);
      } finally {
        loading.value = false;
      }
    };
    
    const editStatus = (status) => {
      editingStatus.value = { ...status };
    };
    
    const saveStatus = async () => {
      try {
        if (!editingStatus.value || !editingStatus.value.name) {
          return;
        }
        
        loading.value = true;
        const response = await axios.put(`/statuses/${editingStatus.value._id}`, {
          name: editingStatus.value.name,
          color: editingStatus.value.color
        });
        
        // Обновляем статус в списке
        const index = statuses.value.findIndex(s => s._id === response.data._id);
        if (index !== -1) {
          statuses.value[index] = response.data;
        }
        
        editingStatus.value = null;
      } catch (error) {
        console.error('Ошибка при обновлении статуса:', error);
      } finally {
        loading.value = false;
      }
    };
    
    const cancelEdit = () => {
      editingStatus.value = null;
    };
    
    const confirmDeleteStatus = (status) => {
      statusToDelete.value = status;
      showDeleteConfirm.value = true;
    };
    
    const deleteStatus = async () => {
      try {
        if (!statusToDelete.value) {
          return;
        }
        
        loading.value = true;
        await axios.delete(`/statuses/${statusToDelete.value._id}`);
        
        // Удаляем статус из списка
        statuses.value = statuses.value.filter(s => s._id !== statusToDelete.value._id);
        
        showDeleteConfirm.value = false;
        statusToDelete.value = null;
      } catch (error) {
        console.error('Ошибка при удалении статуса:', error);
      } finally {
        loading.value = false;
      }
    };
    
    const cancelDelete = () => {
      showDeleteConfirm.value = false;
      statusToDelete.value = null;
    };
    
    onMounted(() => {
      fetchStatuses();
    });
    
    return {
      statuses,
      loading,
      error,
      showNewStatusForm,
      newStatus,
      editingStatus,
      showDeleteConfirm,
      statusToDelete,
      fetchStatuses,
      createStatus,
      editStatus,
      saveStatus,
      cancelEdit,
      confirmDeleteStatus,
      deleteStatus,
      cancelDelete
    };
  }
};
</script>

<style scoped>
.statuses-container {
  padding: 20px;
  height: 100%;
  overflow-y: auto;
}

h1 {
  margin-bottom: 20px;
  color: #333;
}

.statuses-content {
  display: flex;
  gap: 20px;
}

.statuses-list {
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.statuses-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.add-status-button {
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.add-status-button i {
  margin-right: 5px;
}

.add-status-button:hover {
  background-color: #388e3c;
}

.loading, .no-statuses {
  padding: 20px;
  text-align: center;
  color: #666;
}

.statuses-table-container {
  overflow-x: auto;
}

.statuses-table {
  width: 100%;
  border-collapse: collapse;
}

.statuses-table th,
.statuses-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.statuses-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.status-color {
  display: flex;
  align-items: center;
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  margin-right: 10px;
  border: 1px solid #ddd;
}

.color-value {
  font-family: monospace;
}

.status-actions {
  display: flex;
  gap: 10px;
}

.edit-button, .delete-button, .save-button, .cancel-button {
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.edit-button {
  background-color: #2196f3;
  color: white;
}

.edit-button:hover {
  background-color: #1976d2;
}

.delete-button {
  background-color: #f44336;
  color: white;
}

.delete-button:hover {
  background-color: #d32f2f;
}

.save-button {
  background-color: #4caf50;
  color: white;
}

.save-button:hover {
  background-color: #388e3c;
}

.cancel-button {
  background-color: #9e9e9e;
  color: white;
}

.cancel-button:hover {
  background-color: #757575;
}

.edit-button i, .delete-button i, .save-button i, .cancel-button i {
  margin-right: 5px;
}

.edit-field {
  display: flex;
  align-items: center;
}

.edit-field input[type="text"] {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.edit-field input[type="color"] {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.new-status-form {
  width: 300px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input[type="text"] {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-group input[type="color"] {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  vertical-align: middle;
  margin-right: 10px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.create-button {
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  flex: 1;
}

.create-button:disabled {
  background-color: #9e9e9e;
  cursor: not-allowed;
}

.create-button i {
  margin-right: 5px;
}

.delete-confirm-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.delete-confirm-content {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 20px;
  width: 400px;
  max-width: 90%;
}

.delete-confirm-content h3 {
  margin-bottom: 10px;
  color: #333;
}

.delete-confirm-content p {
  margin-bottom: 20px;
  color: #666;
}

.delete-confirm-actions {
  display: flex;
  gap: 10px;
}

.confirm-button {
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  flex: 1;
}

.confirm-button:hover {
  background-color: #d32f2f;
}

.confirm-button i {
  margin-right: 5px;
}

@media (max-width: 768px) {
  .statuses-content {
    flex-direction: column;
  }
  
  .new-status-form {
    width: 100%;
  }
}
</style> 